import React, { useState } from 'react';
import { Building2, MapPin, Users, Calendar, Globe, Heart, Instagram, Linkedin } from 'lucide-react';
import TikTokIcon from './TikTokIcon';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export function socialHref(url: string | null | undefined): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
}

function socialHandle(url: string | null | undefined, platform: 'tiktok' | 'instagram' | 'linkedin'): string {
  if (!url) return '';
  const trimmed = url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
  if (platform === 'tiktok') {
    const m = trimmed.match(/tiktok\.com\/@?([^/?#]+)/i);
    return m ? `@${m[1]}` : trimmed;
  }
  if (platform === 'instagram') {
    const m = trimmed.match(/instagram\.com\/([^/?#]+)/i);
    return m ? `@${m[1]}` : trimmed;
  }
  const m = trimmed.match(/linkedin\.com\/(?:company|in|school)\/([^/?#]+)/i);
  return m ? m[1] : trimmed;
}

export interface CompanyCardData {
  name: string;
  logo_url?: string | null;
  bio?: string | null;
  industry?: string | null;
  size?: string | null;
  location?: string | null;
  website?: string | null;
  cevee_tags?: string[];
  looking_for?: string | null;
  founded_year?: number | null;
  instagram_url?: string | null;
  tiktok_url?: string | null;
  linkedin_url?: string | null;
}

interface CompanyCardProps {
  company: CompanyCardData;
  showActions?: boolean;
  translateTag?: (tag: string) => string;
  onTagClick?: (tag: string) => void;
  onIndustryClick?: (industry: string) => void;
  onLocationClick?: (location: string) => void;
  renderActions?: () => React.ReactNode;
  aboutLabel?: string;
  lookingForLabel?: string;
  interestedLabel?: string;
  readMoreLabel?: string;
  readLessLabel?: string;
}

export default function CompanyCard({
  company,
  showActions = true,
  translateTag,
  onTagClick,
  onIndustryClick,
  onLocationClick,
  renderActions,
  aboutLabel = 'About us',
  lookingForLabel = 'Looking for',
  interestedLabel = 'Interested?',
  readMoreLabel = 'Read more',
  readLessLabel = 'Less',
}: CompanyCardProps) {
  const [bioExpanded, setBioExpanded] = useState(false);
  const [lookingForExpanded, setLookingForExpanded] = useState(false);

  const logoUrl = company.logo_url
    ? company.logo_url.startsWith('http') || company.logo_url.startsWith('/') || company.logo_url.startsWith('blob:')
      ? company.logo_url
      : `${API_BASE}/api/v1/companies/logos/${company.logo_url}`
    : null;

  const bioLong = (company.bio?.length || 0) > 120 || (company.bio?.match(/\n/g) || []).length >= 3;
  const lookingForLong = (company.looking_for?.length || 0) > 80 || (company.looking_for?.match(/\n/g) || []).length >= 2;
  const tt = translateTag || ((tag: string) => tag);

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 sm:px-5 sm:pt-5 sm:pb-3 flex items-start gap-3 sm:gap-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={company.name}
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover shadow-sm border border-slate-100"
          />
        ) : (
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center shadow-sm">
            <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-pink-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate">{company.name}</h3>
          {company.industry && (
            <button
              onClick={() => onIndustryClick?.(company.industry!)}
              className={`text-[10px] sm:text-xs mt-0.5 text-left text-slate-500 ${onIndustryClick ? 'hover:text-pink-500 transition-colors cursor-pointer' : 'cursor-default'}`}
            >
              {company.industry}
            </button>
          )}
          <div className="flex items-center flex-wrap gap-x-2 sm:gap-x-3 gap-y-0.5 sm:gap-y-1 mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-slate-400">
            {company.location && (
              <button
                onClick={() => onLocationClick?.(company.location!)}
                className={`flex items-center gap-0.5 sm:gap-1 ${onLocationClick ? 'hover:text-pink-500 transition-colors cursor-pointer' : 'cursor-default'}`}
              >
                <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {company.location}
              </button>
            )}
            {company.size && (
              <span className="flex items-center gap-0.5 sm:gap-1">
                <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {company.size}
              </span>
            )}
            {company.founded_year && (
              <span className="flex items-center gap-0.5 sm:gap-1">
                <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {company.founded_year}
              </span>
            )}
          </div>
          {(company.cevee_tags || []).length > 0 && (
            <div className="flex flex-wrap gap-0.5 sm:gap-1 mt-1 sm:mt-1.5">
              {company.cevee_tags!.slice(0, 5).map((tag) => (
                <button
                  key={tag}
                  onClick={() => onTagClick?.(tag)}
                  className={`text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded-full font-medium transition-all bg-gradient-to-r from-pink-600 to-pink-500 text-white ${onTagClick ? 'hover:from-pink-700 hover:to-pink-600 cursor-pointer' : 'cursor-default'}`}
                >
                  {tt(tag)}
                </button>
              ))}
              {company.cevee_tags!.length > 5 && (
                <span className="text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400">
                  +{company.cevee_tags!.length - 5}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bio */}
      {company.bio && (
        <div className="px-3 sm:px-5">
          <h4 className="text-[9px] sm:text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 sm:mb-1">{aboutLabel}</h4>
          <p className={`text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line ${!bioExpanded ? 'line-clamp-3' : ''}`}>
            {company.bio}
          </p>
          {bioLong && (
            <button
              onClick={() => setBioExpanded(!bioExpanded)}
              className="text-[10px] sm:text-xs text-pink-500 hover:text-pink-600 font-medium mt-0.5"
            >
              {bioExpanded ? readLessLabel : readMoreLabel}
            </button>
          )}
        </div>
      )}

      {/* Looking for */}
      {company.looking_for && (
        <div className="px-3 sm:px-5 pt-1.5 sm:pt-2">
          <h4 className="text-[9px] sm:text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 sm:mb-1">{lookingForLabel}</h4>
          <p className={`text-xs sm:text-sm text-slate-600 leading-relaxed whitespace-pre-line ${!lookingForExpanded ? 'line-clamp-2' : ''}`}>
            {company.looking_for}
          </p>
          {lookingForLong && (
            <button
              onClick={() => setLookingForExpanded(!lookingForExpanded)}
              className="text-[10px] sm:text-xs text-pink-500 hover:text-pink-600 font-medium mt-0.5"
            >
              {lookingForExpanded ? readLessLabel : readMoreLabel}
            </button>
          )}
        </div>
      )}

      {/* Footer: Website + socials left, Interested right */}
      <div className="mt-auto px-3 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {company.website ? (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs text-slate-400 hover:text-pink-500 transition-colors min-w-0"
            >
              <Globe className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
              <span className="truncate">
                {company.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </span>
            </a>
          ) : <div />}
          {(company.tiktok_url || company.instagram_url || company.linkedin_url) && (
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              {company.tiktok_url && (
                <a
                  href={socialHref(company.tiktok_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  title={`TikTok · ${socialHandle(company.tiktok_url, 'tiktok')}`}
                  className="text-slate-400 hover:text-pink-500 transition-colors"
                >
                  <TikTokIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              )}
              {company.instagram_url && (
                <a
                  href={socialHref(company.instagram_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  title={`Instagram · ${socialHandle(company.instagram_url, 'instagram')}`}
                  className="text-slate-400 hover:text-pink-500 transition-colors"
                >
                  <Instagram className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              )}
              {company.linkedin_url && (
                <a
                  href={socialHref(company.linkedin_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  title={`LinkedIn · ${socialHandle(company.linkedin_url, 'linkedin')}`}
                  className="text-slate-400 hover:text-pink-500 transition-colors"
                >
                  <Linkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              )}
            </div>
          )}
        </div>
        {showActions && (
          renderActions ? renderActions() : (
            <span className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium bg-slate-50 text-slate-400 border-2 border-slate-200 cursor-default shrink-0">
              <Heart className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> {interestedLabel}
            </span>
          )
        )}
      </div>
    </div>
  );
}
